import { useRef } from 'react'
import { useStore } from 'react-redux'

import moment from 'moment'

import { bindActionCreators } from 'redux'

import * as firewallLogsMapping from '@features/store/actions/firewallLogs'
import * as firewallLogsMappingType from '@features/store/actions/firewallLogs/types'
import FirewallLogsApi from '@features/api/logs'

import { useAppLoader } from '@services/hooks/AppLoader'
import { useApiError } from '@services/hooks/ApiError'
import { AWS, AZURE, useLandingZones } from '@features/hooks/LandingZones'

import { createId } from '@services/utils'

export const FIREWALL = 'firewall'
export const NSGFLOW = 'nsgflow'

export const useLogsFirewall = () => {
    const store = useStore()
    const firewallLogsAction = bindActionCreators(firewallLogsMapping, store.dispatch)

    const { hide: hideLoader, show: showLoader } = useAppLoader()
    const { handleError: apiError } = useApiError()
    const { getProvider } = useLandingZones()

    const searchCallback = useRef(null)

    const getData = (index = null) => {
        if (!index) return store.getState().firewallLogs
        return store.getState().firewallLogs[index]
    }

    const getQueryString = (params, sort = true, pagination = { offset: 0, limit: 25 }) => {
        const result = []
        for (let key in params) {
            const value = params[key]
            if (!value
                || value === undefined
                || ((typeof value === 'number') && isNaN(value))
                || value === 'all') continue

            if (value instanceof Date)
                result.push(`${encodeURIComponent(key)}=${encodeURIComponent(value.toISOString())}`)
            else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
                const dateObj = new Date(value)
                if (!isNaN(dateObj.getTime()))
                    result.push(`${encodeURIComponent(key)}=${encodeURIComponent(dateObj.toISOString())}`)
            }
            else if (key === 'id') result.push(`lzId=${encodeURIComponent(value)}`)
            else result.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        }

        if (sort) {
            result.push(`sortColumn=TimeGenerated`)
            result.push(`sortDirection=Desc`)
        }

        if (pagination) {
            result.push(`skip=${pagination.offset}`)
            result.push(`take=${pagination.limit}`)
        }

        return result.join('&')
    }

    const searchAzure = (id, query, page = 0) => {
        FirewallLogsApi.search(`/api/landing-zones/${id}/firewall-logs?${query}`, AZURE, 'logs', '/user_impersonation', { 'x-api-version': '4.0' }, {
            success: (result) => {
                firewallLogsAction.populate(result?.data?.pageItems ?? [], () => {
                    const pagination = { page: page, itemsCount: result?.data?.itemCount ?? 0, controlled: true }
                    computeLogs(pagination, id, AZURE)
                })
            },
            error: (result) => { apiError(result.data.message) }
        })
    }

    const searchFlow = (id, query, page = 0) => {
        FirewallLogsApi.search(`/api/landing-zones/${id}/flow-logs?${query}`, AZURE, 'logs', '/user_impersonation', { 'x-api-version': '4.0' }, {
            success: (result) => {
                firewallLogsAction.populate(result?.data?.pageItems ?? [], () => {
                    const pagination = { page: page, itemsCount: result?.data?.itemCount ?? 0, controlled: true }
                    computeLogs(pagination, id, AZURE, NSGFLOW)
                })
            },
            error: (result) => { apiError(result.data.message) }
        })
    }

    const searchAws = (query, id) => {
        FirewallLogsApi.search(`/landing-zones/${id}/firewall-logs?${query}`, AWS, 'logs', null, {}, {
            success: (result) => {
                // eslint-disable-next-line
                firewallLogsAction.populate(result?.data?.firewall_logs ?? [], () => {
                    // eslint-disable-next-line
                    const pagination = { page: 0, itemsCount: result?.data?.firewall_logs?.events?.length ?? 0, controlled: false }
                    computeLogs(pagination, id, AWS, FIREWALL, result?.data?.firewall_logs?.nextToken || null)
                })
            },
            error: (result) => { apiError(result.data.response.data.errorMessage) }
        })
    }

    const search = (id, values = {}, callback = null, mode = FIREWALL) => {
        showLoader(' ')
        searchCallback.current = callback

        const provider = getProvider(id)

        values = (provider === AZURE) ? { ...values, ...{ id: id } } : values
        firewallLogsAction.setFormat(values, firewallLogsMappingType.SET_FILTERS)

        const query = getQueryString(
            values,
            (provider === AZURE) ? true : false,
            (provider === AWS) ? null : { offset: 0, limit: 25 }
        )

        if (provider === AZURE && mode === NSGFLOW) searchFlow(id, query)
        else if (provider === AZURE) searchAzure(id, query)
        else if (provider === AWS) searchAws(query, id)
        else return hideLoader()
    }

    const transformValue = (value, mode) => {
        if (mode === 'timestamp')
            return moment.unix(value).utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSSZ')
    }

    const computeAwsAction = (item) => {
        const state = item?.event?.netflow?.state || null
        const proto = item?.event?.proto || null
        const reason = item?.event?.netflow?.reason || null
        const alerted = item?.event?.netflow?.alerted || false

        let result = 'Allow'

        if (((state === 'established' || state === 'closed') && proto !== 'unknown')
            || (state === 'new' && reason === 'timeout' && !alerted)) result = 'Allow'
        else if (state === 'new' && reason === 'timeout' && alerted) result = 'Deny'

        return result
    }

    const computeLogs = (pagination = { page: 0, itemsCount: 0, controlled: true }, id, provider, mode = FIREWALL, nextToken = null) => {
        let data = getData('data')
        const grid = []

        if (provider === AWS) data = data.events

        for (let i = 0; i < data.length; i++) {
            let item = data[i]
            if (provider === AWS) item = JSON.parse(item.message)

            const action = (provider === AZURE) ? item.action ?? null : computeAwsAction(item)

            grid.push({
                id: createId(5),
                // eslint-disable-next-line
                date: (provider === AZURE) ? item.timeGenerated ?? null : transformValue(item.event_timestamp, 'timestamp'),
                /**
                 * disable collapse for Azure when action is Deny
                 */
                isCollapseDisabled: (provider === AZURE && action === 'Deny'),
                action: action,
                // eslint-disable-next-line
                region: (provider === AZURE) ? item.region ?? null : item.availability_zone ?? null,
                category: (provider === AZURE) ? item.category ?? null : '-',
                flowStartTime: (mode === NSGFLOW) ? item.flowStartTime ?? null : null,
                flowEndTime: (mode === NSGFLOW) ? item.flowEndTime ?? null : null,
                flowType: (mode === NSGFLOW) ? item.flowType ?? null : null,
                flowDirection: (mode === NSGFLOW) ? item.flowDirection ?? null : null,
                protocol: (provider === AZURE) ? item.protocol ?? null : item.event?.proto ?? null,
                // eslint-disable-next-line
                source: (provider === AZURE) ? item.source ?? null : `${item.event?.src_ip ?? null}:${item.event?.src_port ?? null}`,
                // eslint-disable-next-line
                target: (provider === AZURE) ? item.target ?? null : `${item.event?.dest_ip ?? null}:${item.event?.dest_port ?? null}`,
                reason: (provider === AZURE) ? item.reason ?? null : item.event?.netflow?.reason ?? null,
                data: {
                    ...(provider === AWS && { json: item.event }),
                    provider: String(provider),
                    // eslint-disable-next-line
                    policy: (provider === AZURE) ? item.policy ?? null : item.firewall_name ?? null,
                    // eslint-disable-next-line
                    ruleCollection: (provider === AZURE) ? item.ruleCollection ?? null : item.event?.pkt_src ?? null,
                    ruleCollectionGroup: (provider === AZURE) ? item.ruleCollectionGroup ?? null : null,
                    // eslint-disable-next-line
                    rule: (provider === AZURE) ? item.rule ?? null : item.event?.event_type ?? null,
                    resourceGroup: (mode === NSGFLOW) ? item.resourceGroup ?? null : null,
                    subscription: (mode === NSGFLOW) ? item.subscriptionId ?? null : null,
                    virtualNetwork: (mode === NSGFLOW) ? item.virtualNetwork ?? null : null
                }
            })
        }

        firewallLogsAction.setFormat({
            list: grid,
            pagination: { ...pagination, nextToken: nextToken },
            selected: id
        }, firewallLogsMappingType.SET_FORMAT, () => {
            hideLoader()
            if (searchCallback.current) searchCallback.current()
        })
    }

    const paginate = (page, callback = null, mode = FIREWALL) => {
        showLoader(' ')
        searchCallback.current = callback

        const filters = getData('filters')
        const query = getQueryString(filters, true, { offset: parseInt(page) * 25, limit: 25 });

        (mode === FIREWALL)
            ? searchAzure(filters.id, query, page)
            : searchFlow(filters.id, query, page)
    }

    const reset = (callback = null) => {
        searchCallback.current = callback

        firewallLogsAction.setFormat({
            list: [],
            pagination: { page: 0, itemsCount: 0, controlled: true },
            selected: null
        }, firewallLogsMappingType.SET_FORMAT, () => {
            firewallLogsAction.setFormat({}, firewallLogsMappingType.SET_FILTERS, () => {
                hideLoader()
                if (searchCallback.current) searchCallback.current()
            })
        })
    }

    return { search, paginate, reset }
}